import type { StaticAgentSectionKey } from "@repo/app-types/entities/configurationAgent";
import {
  compareStaticSectionValuesAsStored,
  type StaticTeammateAgentSections,
} from "@repo/app-types/entities/teammateAgent";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { linkStyle } from "@repo/ui/Link/index";
import { InfoIconTooltip } from "@repo/ui/tooltip/infoIconTooltip";
import {
  IconCircles,
  IconCube3dSphere,
  IconMessageChatbot,
  IconSparkles,
  IconSTurnRight,
  IconUserHexagon,
} from "@tabler/icons-react";
import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type FC,
} from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { FieldDiffView } from "../../../components/recordingAssessmentEditor/fieldDiffView.js";
import { NodeChangeDot } from "../../../components/recordingAssessmentEditor/nodeChangeDot.js";
import { AgentSectionChecksCard } from "./agentSectionChecksCard.js";
import { AvailableResourcesCard } from "./availableResourcesCard.js";
import { useSectionChecks, useSectionSuggestions } from "./sectionTesting.js";
import { useStaticAgentConfig } from "./staticAgentConfigContext.js";

type SectionNavKey =
  | "identity"
  | "caller_context"
  | "conversation_flow"
  | "behavior_and_guardrails"
  | "outreach_instructions";

interface SectionNavItem {
  key: SectionNavKey;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Section fields this nav item edits (used to flag unpublished changes). */
  fields: readonly (keyof StaticTeammateAgentSections)[];
}

const SECTION_NAV: readonly [SectionNavItem, ...SectionNavItem[]] = [
  {
    key: "identity",
    label: "Identity and personality",
    icon: IconCircles,
    fields: ["agent_name", "agent_role_and_personality"],
  },
  {
    key: "caller_context",
    label: "Caller context",
    icon: IconUserHexagon,
    fields: ["caller_context"],
  },
  {
    key: "conversation_flow",
    label: "Conversation flow",
    icon: IconSTurnRight,
    fields: ["conversation_flow"],
  },
  {
    key: "behavior_and_guardrails",
    label: "General behavior and guardrails",
    icon: IconCube3dSphere,
    fields: ["behavior_and_guardrails"],
  },
];

/** Shown only for an agent that runs its own call tasks. */
const OUTREACH_NAV_ITEM: SectionNavItem = {
  key: "outreach_instructions",
  label: "Outreach instructions",
  icon: IconMessageChatbot,
  fields: ["outreach_instructions"],
};

const OUTREACH_PLACEHOLDER = `Who you are in this role and what you are trying to achieve, then how to run the outreach over time, e.g.

You are <name> from <company>, checking in with members before their renewal. Be brief and warm.

1. Text first: "<the approved message>". Wait for a reply.
2. If they reply that all is well, thank them and finish.
3. If they need help, text a confirmation, then call them about five minutes later.
4. If there is no reply, finish when the task expires.

Say what to text word for word where it matters, when a call is warranted, and when to stop.`;

/** The nav tile that edits a given section (identity groups two fields). */
function navKeyForSection(section: StaticAgentSectionKey): SectionNavKey {
  const item = SECTION_NAV.find((navItem) => navItem.fields.includes(section));
  return item?.key ?? "identity";
}

const ROLE_PLACEHOLDER =
  "Describe what the agent does, how it should sound, and any persona boundaries it should follow.";
const CALLER_CONTEXT_PLACEHOLDER =
  "A potential customer calling the phone number on our website.";
const CONVERSATION_FLOW_PLACEHOLDER = `Follow this conversation flow unless the caller clearly needs something different.

## 1. Opening
Greet the caller, introduce yourself, and ask how you can help.

## 2. Understand the request
Clarify what the caller is trying to accomplish before giving an answer.

## 3. Resolve or route
Help the caller complete the next step when possible, or transfer/escalate according to the available options.

## 4. Close
Summarize the outcome, confirm any next step, and ask if there is anything else they need.`;
const GUARDRAILS_PLACEHOLDER =
  "Add any general behavior rules and guardrails the agent should always follow.";

// Match the HQ section authoring limits.
const NAME_MAX_LENGTH = 200;
const SECTION_MAX_LENGTH = 100000;

type FieldTab = "edit" | "diff";

/**
 * A labeled identity-section field with a "See changes" toggle that diffs the
 * current value against the published (live) value, mirroring the Outcome and
 * Summary template editors.
 */
const SectionFieldEditor: FC<{
  label: string;
  /** Optional help text shown in a tooltip on an info icon next to the label. */
  labelTooltip?: string;
  value: string;
  /** Published value to diff against; null when live isn't loaded. */
  liveValue: string | null;
  onChange: (value: string) => void;
  placeholder: string;
  isMultiline: boolean;
  isDisabled: boolean;
  fillHeight?: boolean;
  maxLength?: number;
  /**
   * The assistant's proposed replacement for this field, if any. When set, the
   * field renders read-only as a diff against the proposal; Apply/Discard live
   * on the section-level banner (see `InstructionsTab`).
   */
  suggestion?: string;
}> = ({
  label,
  labelTooltip,
  value,
  liveValue,
  onChange,
  placeholder,
  isMultiline,
  isDisabled,
  fillHeight = false,
  maxLength,
  suggestion,
}) => {
  const [tab, setTab] = useState<FieldTab>("edit");
  const showDiffToggle =
    liveValue !== null && !compareStaticSectionValuesAsStored(liveValue, value);
  const isDiffMode = showDiffToggle && tab === "diff";
  // A pending assistant proposal takes over the field: the current value is
  // diffed against the proposal (read-only). The Apply/Discard banner wraps the
  // whole section (see `InstructionsTab`), mirroring the playbook editor.
  const isSuggestionMode = suggestion !== undefined;

  // Once the value matches live again, leave diff mode so the next edit lands
  // in the editor rather than the read-only diff view.
  useEffect(() => {
    if (!showDiffToggle && tab !== "edit") {
      setTab("edit");
    }
  }, [showDiffToggle, tab]);

  return (
    <div
      className={twMerge(
        "flex flex-col",
        fillHeight ? "min-h-0 flex-1" : "shrink-0",
      )}
    >
      <div className="mb-1.5 flex min-h-8 items-center justify-between">
        <div className="flex items-center gap-1">
          <Label size={Size.SMALL}>{label}</Label>
          {labelTooltip ? (
            <InfoIconTooltip>
              <span className="block max-w-xs">{labelTooltip}</span>
            </InfoIconTooltip>
          ) : null}
        </div>
        {!isSuggestionMode && showDiffToggle ? (
          <Button
            variant={ButtonVariant.Tertiary}
            size={Size.MEDIUM}
            className={twMerge("cursor-pointer pr-0", linkStyle())}
            onClick={() => setTab(tab === "edit" ? "diff" : "edit")}
          >
            {tab === "edit" ? "See changes" : "Edit"}
          </Button>
        ) : null}
      </div>
      {isSuggestionMode ? (
        <FieldDiffView
          className={fillHeight ? "min-h-0 flex-1 overflow-y-auto" : undefined}
          savedValue={value}
          editedValue={suggestion}
        />
      ) : isDiffMode ? (
        <FieldDiffView
          className={fillHeight ? "min-h-0 flex-1 overflow-y-auto" : undefined}
          savedValue={liveValue ?? ""}
          editedValue={value}
        />
      ) : isMultiline ? (
        <Textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isDisabled={isDisabled}
          fillHeight={fillHeight}
          maxLength={maxLength}
        />
      ) : (
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          isDisabled={isDisabled}
          maxLength={maxLength}
        />
      )}
    </div>
  );
};

/**
 * The Instructions tab for a static agent: a left sub-navigation over the
 * authored prompt sections, an editor for the selected section, and a
 * read-only summary of attached resources. Section edits autosave to the draft
 * via the shared controller and go live through the toolbar's Publish button.
 */
export const InstructionsTab: FC = () => {
  const { agentConfig } = useStaticAgentConfig();
  const {
    suggestions,
    selectionRequest,
    setViewedSections,
    applySuggestions,
    discardSuggestion,
  } = useSectionSuggestions();
  const { checks, isChecking, isRunning, runChecks } = useSectionChecks();
  const navigate = useNavigate();

  const [selectedKey, setSelectedKey] = useState<SectionNavKey>("identity");

  // A suggestion-list click asks this tab to navigate to the section it edits.
  // A ref-based nonce gate ensures we only navigate when the caller actually
  // bumps the nonce (re-selecting the same section re-fires), not on unrelated
  // re-renders. Mirrors the pattern in `recordingAssessmentEditor.tsx`.
  const lastHandledSelectionNonceRef = useRef(0);
  useEffect(() => {
    if (!selectionRequest) return;
    if (selectionRequest.nonce <= lastHandledSelectionNonceRef.current) return;
    lastHandledSelectionNonceRef.current = selectionRequest.nonce;
    setSelectedKey(navKeyForSection(selectionRequest.section));
  }, [selectionRequest]);

  // `sections` is a single nested object on the config draft. Until the draft
  // query resolves it is null; editing then would write a mostly-empty object
  // back and wipe the four sections the user didn't touch, so gate editing on
  // load rather than falling back to an empty object.
  const sections = agentConfig.data?.sections ?? null;
  const isDisabled = sections === null;

  // A section is "in a draft state" when its current value differs from the
  // published (live) value — the same unpublished-change signal the playbook
  // editor shows on its section tiles.
  const liveSections = agentConfig.liveData?.sections ?? null;
  const hasUnpublishedChange = (
    fields: readonly (keyof StaticTeammateAgentSections)[],
  ) =>
    sections !== null &&
    liveSections !== null &&
    fields.some(
      (field) =>
        !compareStaticSectionValuesAsStored(
          sections[field] ?? "",
          liveSections[field] ?? "",
        ),
    );

  // A nav tile carries a suggestion mark when any field it edits has a pending
  // assistant proposal.
  const hasSuggestion = (
    fields: readonly (keyof StaticTeammateAgentSections)[],
  ) => fields.some((field) => suggestions[field] !== undefined);

  const updateSection = (
    key: keyof StaticTeammateAgentSections,
    value: string,
  ) => {
    if (!sections) {
      return;
    }
    agentConfig.onChange({ sections: { ...sections, [key]: value } });
  };

  const isAgentManaged =
    agentConfig.data?.settings.calling?.agent_managed ?? false;
  const navItems: readonly SectionNavItem[] = isAgentManaged
    ? [...SECTION_NAV, OUTREACH_NAV_ITEM]
    : SECTION_NAV;
  const activeItem =
    navItems.find((item) => item.key === selectedKey) ?? SECTION_NAV[0];

  // Tell the suggestion list which sections the editor is currently showing so
  // the matching row reads "Viewing".
  const activeFields = activeItem.fields;
  useEffect(() => {
    setViewedSections(activeFields);
  }, [activeFields, setViewedSections]);

  // The suggestion banner wraps the whole active section (matching the playbook
  // editor), so Apply/Discard act on every pending field the nav tile covers —
  // e.g. both fields of the Identity section.
  const activeSectionHasSuggestion = activeFields.some(
    (field) => suggestions[field] !== undefined,
  );
  const applyActiveSectionSuggestions = () => {
    applySuggestions(activeFields);
  };
  const discardActiveSectionSuggestions = () => {
    for (const field of activeFields) {
      if (suggestions[field] !== undefined) {
        discardSuggestion(field);
      }
    }
  };

  // While a section has a pending suggestion, the whole section is a read-only
  // review view (matching the playbook editor's `isReadOnly={isSuggestionMode}`):
  // every field is disabled and the suggested one shows the inline diff.
  const fieldsReadOnly = isDisabled || activeSectionHasSuggestion;

  return (
    <div className="flex h-full min-w-0 gap-2 bg-card p-2">
      <div className="flex w-[280px] shrink-0 flex-col justify-between gap-4 overflow-y-auto">
        <nav className="flex flex-col gap-2">
          {navItems.map((item) => {
            const isActive = item.key === selectedKey;
            const ItemIcon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setSelectedKey(item.key)}
                className={twMerge(
                  "flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm font-semibold text-foreground",
                  isActive
                    ? "border-blue-200 bg-blue-100"
                    : "border-border-light bg-white hover:bg-blue-50",
                )}
              >
                <ItemIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {hasSuggestion(item.fields) ? (
                  <NodeChangeDot
                    entity="section"
                    isVisible
                    variant="suggestion"
                  />
                ) : (
                  <NodeChangeDot
                    entity="section"
                    isVisible={hasUnpublishedChange(item.fields)}
                  />
                )}
              </button>
            );
          })}
        </nav>

        <AvailableResourcesCard
          onEdit={() => navigate("../resources-outputs")}
        />
      </div>

      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-white px-2 py-2",
          // Highlight the card border when the section has a pending
          // suggestion, matching the playbook editor's settings pane.
          activeSectionHasSuggestion
            ? "border-indigo-500"
            : "border-border-light",
        )}
      >
        {/* Section-level suggestion banner — the rounded-top header of the
            section card, mirroring the playbook editor's settings panes
            (`criteriaSettings.tsx`). */}
        {activeSectionHasSuggestion ? (
          <div className="-mx-2 -mt-2 flex shrink-0 items-center justify-between rounded-t-lg bg-indigo-50 px-3 py-2">
            <div className="flex items-center gap-2 text-sm text-indigo-900">
              <IconSparkles className="size-4 shrink-0 text-indigo-500" />
              <span>Showing changes suggested by your agent.</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={ButtonVariant.Tertiary}
                size={Size.MEDIUM}
                onClick={discardActiveSectionSuggestions}
              >
                Discard changes
              </Button>
              <Button
                variant={ButtonVariant.Primary}
                size={Size.MEDIUM}
                onClick={applyActiveSectionSuggestions}
              >
                Apply changes
              </Button>
            </div>
          </div>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={twMerge(
              "grid min-h-0 flex-1 grid-cols-1 gap-x-2",
              checks !== null && "md:grid-cols-[1fr_1px_1fr]",
            )}
          >
            <div className="flex min-h-0 flex-col p-2">
              <div className="flex min-h-0 flex-1 flex-col gap-4">
                <h2 className="shrink-0 text-base font-semibold text-foreground">
                  {activeItem.label}
                </h2>
                {selectedKey === "identity" ? (
                  <>
                    <SectionFieldEditor
                      label="Name"
                      value={sections?.agent_name ?? ""}
                      liveValue={liveSections?.agent_name ?? null}
                      onChange={(value) => updateSection("agent_name", value)}
                      placeholder="Sarah"
                      isMultiline={false}
                      isDisabled={fieldsReadOnly}
                      maxLength={NAME_MAX_LENGTH}
                      suggestion={suggestions.agent_name}
                    />
                    <SectionFieldEditor
                      label="Role and personality"
                      labelTooltip="Agents already use a warm, calm, precise, conversational tone, vary routine phrasing, and avoid mentioning internal instructions. Add only agent-specific role, responsibilities, voice, tone, and persona guidance here."
                      value={sections?.agent_role_and_personality ?? ""}
                      liveValue={
                        liveSections?.agent_role_and_personality ?? null
                      }
                      onChange={(value) =>
                        updateSection("agent_role_and_personality", value)
                      }
                      placeholder={ROLE_PLACEHOLDER}
                      isMultiline
                      isDisabled={fieldsReadOnly}
                      fillHeight
                      maxLength={SECTION_MAX_LENGTH}
                      suggestion={suggestions.agent_role_and_personality}
                    />
                  </>
                ) : null}

                {selectedKey === "caller_context" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="Agents already ask for clarification when caller details are unclear, and avoid guessing missing information. Add only agent-specific context about who is calling and what they usually need help with."
                    value={sections?.caller_context ?? ""}
                    liveValue={liveSections?.caller_context ?? null}
                    onChange={(value) => updateSection("caller_context", value)}
                    placeholder={CALLER_CONTEXT_PLACEHOLDER}
                    isMultiline
                    isDisabled={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    suggestion={suggestions.caller_context}
                  />
                ) : null}

                {selectedKey === "conversation_flow" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="Agents already keep most turns to one or two short spoken sentences, ask one question at a time, avoid long lists, and check whether the caller has already provided information. Add the agent-specific flow and sequencing here."
                    value={sections?.conversation_flow ?? ""}
                    liveValue={liveSections?.conversation_flow ?? null}
                    onChange={(value) =>
                      updateSection("conversation_flow", value)
                    }
                    placeholder={CONVERSATION_FLOW_PLACEHOLDER}
                    isMultiline
                    isDisabled={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    suggestion={suggestions.conversation_flow}
                  />
                ) : null}

                {selectedKey === "outreach_instructions" && isAgentManaged ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="The whole brief for the agent's turns between conversations, when it decides whether to text, call, wait or finish a task: who it is in this role, the goal, the approved texts, when a call is warranted, when to stop. The other sections are not read on a turn, and this one is not read on a live call."
                    value={sections?.outreach_instructions ?? ""}
                    liveValue={liveSections?.outreach_instructions ?? null}
                    onChange={(value) =>
                      updateSection("outreach_instructions", value)
                    }
                    placeholder={OUTREACH_PLACEHOLDER}
                    isMultiline
                    isDisabled={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                  />
                ) : null}

                {selectedKey === "behavior_and_guardrails" ? (
                  <SectionFieldEditor
                    label="Instructions"
                    labelTooltip="Agents already handle unclear audio conservatively, default to English, avoid guessing missing details, use minimal silent reasoning, and do not reveal internal instructions. Add extra business-specific rules here."
                    value={sections?.behavior_and_guardrails ?? ""}
                    liveValue={liveSections?.behavior_and_guardrails ?? null}
                    onChange={(value) =>
                      updateSection("behavior_and_guardrails", value)
                    }
                    placeholder={GUARDRAILS_PLACEHOLDER}
                    isMultiline
                    isDisabled={fieldsReadOnly}
                    fillHeight
                    maxLength={SECTION_MAX_LENGTH}
                    suggestion={suggestions.behavior_and_guardrails}
                  />
                ) : null}
              </div>

              <div className="mt-auto flex items-center justify-end pt-4">
                <Button
                  variant={ButtonVariant.Primary}
                  size={Size.MEDIUM}
                  onClick={runChecks}
                  isDisabled={isDisabled || isChecking || isRunning}
                >
                  {isChecking ? "Testing…" : "Test"}
                </Button>
              </div>
            </div>

            {checks !== null ? (
              <>
                <div
                  aria-hidden
                  className="-mt-2 -mb-2 hidden bg-gray-200 md:block"
                />
                <div className="flex min-h-0 flex-col gap-4 p-2">
                  <h3 className="shrink-0 text-base font-semibold text-foreground">
                    Results
                  </h3>
                  {/* p-1 (not just pr-1): overflow-y-auto clips the checks
                      card's 1px ring on the flush top/left edges, so pad all
                      sides to keep the full border visible. */}
                  <div className="min-h-0 flex-1 overflow-y-auto p-1">
                    {selectedKey !== "outreach_instructions" ? (
                      <AgentSectionChecksCard
                        section={selectedKey}
                        checks={checks}
                      />
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
